import { Alert, Button, Grid, Spin, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
    TbChevronLeft,
    TbChevronRight,
    TbDownload,
    TbExternalLink,
    TbInfoCircle,
    TbX,
} from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { formatBytes } from '../Helpers/diskFormat'
import { canOpenInNewTab } from '../Helpers/diskViewerMode'
import { fileViewerStore as store } from '../Stores/FileViewerStore'
import { DiskArchiveView } from './DiskArchiveView'
import { DiskImageView } from './DiskImageView'
import { DiskNodeIcon } from './DiskNodeIcon'
import { DiskNodeInfoPanel } from './DiskNodeInfoPanel'
import { DiskOfficeView } from './DiskOfficeView'
import { DiskPdfView } from './DiskPdfView'
import { DiskTextView } from './DiskTextView'
import { useDiskFileOpeners } from './useDiskFileOpeners'

import type { DiskNode } from '../Contracts/DiskNode'
import type { DiskViewerMode } from '../Helpers/diskViewerMode'
import type { ReactNode } from 'react'

import styles from './DiskFileViewer.module.css'

/** Пояснення для файлів, які переглядач не показує. */
const FALLBACK_TEXT: Partial<Record<DiskViewerMode, string>> = {
    dangerous:
        'Такі файли (HTML, SVG, XML, JavaScript) браузер виконав би як сторінку, тому вони відкриваються лише скачуванням.',
    tooLarge: 'Файл завеликий для перегляду: у браузері показуємо текст до 2 МБ.',
    unsupported: 'Цей формат не переглядається в браузері.',
}

/** Клавіші, які на цих елементах мають власний зміст (поле введення, програвач). */
const ownsKeys = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement &&
    target.closest('input, textarea, select, video, audio, [contenteditable="true"], [role="slider"]') !== null

const Fallback = ({ node, text, onDownload }: { node: DiskNode; text: string; onDownload: () => void }) => (
    <div className={classNames(styles['center'])}>
        <div className={classNames(styles['fallback'])} data-testid="disk-viewer-fallback">
            <DiskNodeIcon kind={node.kind} size={56} />
            <div className={classNames(styles['fallbackName'])}>{node.name}</div>
            <div className={classNames(styles['fallbackText'])}>{text}</div>
            <Button type="primary" icon={<TbDownload />} onClick={onDownload}>
                Скачати · {formatBytes(node.size)}
            </Button>
        </div>
    </div>
)

/** Вміст за режимом перегляду. */
const ViewerContent = observer(({ compact, onDownload }: { compact: boolean; onDownload: () => void }) => {
    const node = store.node
    const mode = store.mode
    const info = store.info

    if (info.error !== null) {
        return (
            <div className={classNames(styles['center'])}>
                <Alert type="error" showIcon title={info.error} />
            </div>
        )
    }

    if (node === null || mode === null || (store.isContentLoading && store.mediaUrl === null && store.text === null)) {
        return (
            <div className={classNames(styles['center'])}>
                <Spin />
            </div>
        )
    }

    if (store.contentError !== null) {
        return <Fallback node={node} text={store.contentError} onDownload={onDownload} />
    }

    const fallback = FALLBACK_TEXT[mode]
    if (fallback !== undefined) return <Fallback node={node} text={fallback} onDownload={onDownload} />

    if (store.text !== null) return <DiskTextView preview={store.text} />

    if (mode === 'archive') return <DiskArchiveView compact={compact} onDownload={onDownload} />

    // Новий токен — новий iframe: гортання на сусідній документ відкриває його наново
    if (mode === 'office') {
        const session = store.office.session

        return session ? <DiskOfficeView key={session.accessToken} onDownload={onDownload} /> : null
    }

    const url = store.mediaUrl
    if (url === null) return null

    const onError = (): void => void store.mediaFailed()
    const key = `${url}#${store.mediaAttempt}`

    switch (mode) {
        case 'image':
            return <DiskImageView key={key} url={url} name={node.name} onError={onError} />
        case 'pdf':
            return <DiskPdfView key={key} url={url} onExpired={onError} />
        case 'video':
            return (
                <div className={classNames(styles['center'])}>
                    <video
                        key={key}
                        className={classNames(styles['video'])}
                        src={url}
                        controls
                        preload="metadata"
                        playsInline
                        onError={onError}
                        data-testid="disk-viewer-video"
                    />
                </div>
            )
        case 'audio':
            return (
                <div className={classNames(styles['center'])}>
                    <div className={classNames(styles['audioCard'])}>
                        <DiskNodeIcon kind={node.kind} size={56} />
                        <div className={classNames(styles['fallbackName'])}>{node.name}</div>
                        <audio
                            key={key}
                            className={classNames(styles['audio'])}
                            src={url}
                            controls
                            preload="metadata"
                            onError={onError}
                            data-testid="disk-viewer-audio"
                        />
                    </div>
                </div>
            )
        default:
            return null
    }
})

const DARK_STAGE: ReadonlySet<DiskViewerMode> = new Set(['image', 'pdf', 'video', 'audio'])

/**
 * Переглядач файлів диска (`DISK-001` §2.2, §6, наряд Д1.11): шар на весь
 * екран поверх будь-якого модуля. Ставить його каркас один раз
 * (`DiskFileViewerHost`), відкривають — адреса `?file=` на екрані диска або
 * `openDiskFile(uuid)` з інших модулів.
 *
 * Office-документи показує Collabora в режимі перегляду (`DiskOfficeView`),
 * ZIP — перелік записів (`DiskArchiveView`); обидва — усередині цього шару.
 *
 * Шапка: назва, «попередній/наступний» у межах папки (кнопки й стрілки,
 * папки пропускаються), «Скачати», «Відкрити в новій вкладці» (для безпечних
 * типів), «Інформація» (властивості й журнал — `DiskNodeInfoPanel`),
 * закрити (Esc).
 */
export const DiskFileViewer = observer(() => {
    const screens = Grid.useBreakpoint()
    const compact = screens.md === false
    const { download, openInNewTab } = useDiskFileOpeners()
    const dialogRef = useRef<HTMLDivElement>(null)
    const isOpen = store.isOpen
    const node = store.node
    const mode = store.mode
    const adjacent = store.adjacent
    const name = node?.name ?? store.info.node?.name ?? ''

    // Відкрито: фокус у шар (працюють клавіші), сторінка під ним не прокручується;
    // закрито — фокус туди, де був
    useEffect(() => {
        if (!isOpen) return undefined

        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const overflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        dialogRef.current?.focus()

        return () => {
            document.body.style.overflow = overflow
            previous?.focus()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return undefined

        const onKey = (event: KeyboardEvent): void => {
            if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return

            if (event.key === 'Escape') {
                event.preventDefault()
                store.close()

                return
            }

            if (ownsKeys(event.target)) return

            if (event.key === 'ArrowLeft') {
                event.preventDefault()
                store.prev()
            } else if (event.key === 'ArrowRight') {
                event.preventDefault()
                store.next()
            }
        }

        document.addEventListener('keydown', onKey)

        return () => document.removeEventListener('keydown', onKey)
    }, [isOpen])

    if (!isOpen) return null

    // На телефоні підказок немає: після дотику вони лишаються висіти й перекривають сусідні кнопки
    const iconButton = (label: string, icon: ReactNode, onClick: () => void, extra: Record<string, unknown> = {}) => (
        <Tooltip title={compact ? '' : label}>
            <Button type="text" icon={icon} aria-label={label} onClick={onClick} {...extra} />
        </Tooltip>
    )
    const closeButton = iconButton('Закрити (Esc)', <TbX />, () => store.close(), { 'data-testid': 'disk-viewer-close' })

    const fileRef = node ?? (store.uuid ? { uuid: store.uuid, updatedAt: null } : null)
    const onDownload = (): void => {
        if (fileRef) void download(fileRef)
    }

    const infoPanel = store.infoOpen ? (
        <aside
            className={classNames(styles['info'], compact && styles['infoOverlay'])}
            aria-label="Інформація про файл"
        >
            {compact && (
                <div className={classNames(styles['infoTop'])}>
                    {iconButton('Сховати інформацію', <TbX />, () => store.setInfoOpen(false))}
                </div>
            )}
            <DiskNodeInfoPanel info={store.info} />
        </aside>
    ) : null

    return createPortal(
        <div
            ref={dialogRef}
            className={classNames(styles['viewer'])}
            role="dialog"
            aria-modal="true"
            aria-label={name ? `Перегляд: ${name}` : 'Перегляд файлу'}
            tabIndex={-1}
            data-testid="disk-viewer"
        >
            <header className={classNames(styles['bar'], compact && styles['barCompact'])}>
                <div className={classNames(styles['titleBox'])}>
                    {node && <DiskNodeIcon kind={node.kind} size={22} />}
                    <span className={classNames(styles['name'])} title={name} data-testid="disk-viewer-name">
                        {name}
                    </span>
                    {adjacent.count > 1 && adjacent.position > 0 && (
                        <span className={classNames(styles['counter'])} data-testid="disk-viewer-counter">
                            {adjacent.position} з {adjacent.count}
                        </span>
                    )}
                    {/* Телефон: назва з «Закрити» — перший рядок, дії — другий */}
                    {compact && closeButton}
                </div>
                <div className={classNames(styles['actions'])}>
                    {adjacent.count > 1 && (
                        <>
                            {iconButton('Попередній файл (←)', <TbChevronLeft />, () => store.prev(), {
                                disabled: adjacent.prev === null,
                                'data-testid': 'disk-viewer-prev',
                            })}
                            {iconButton('Наступний файл (→)', <TbChevronRight />, () => store.next(), {
                                disabled: adjacent.next === null,
                                'data-testid': 'disk-viewer-next',
                            })}
                            <span className={classNames(styles['divider'])} aria-hidden />
                        </>
                    )}
                    {compact ? (
                        iconButton('Скачати', <TbDownload />, onDownload, { 'data-testid': 'disk-viewer-download' })
                    ) : (
                        <Button icon={<TbDownload />} onClick={onDownload} data-testid="disk-viewer-download">
                            Скачати
                        </Button>
                    )}
                    {node &&
                        mode !== null &&
                        canOpenInNewTab(mode) &&
                        iconButton('Відкрити в новій вкладці', <TbExternalLink />, () => void openInNewTab(node), {
                            'data-testid': 'disk-viewer-new-tab',
                        })}
                    {iconButton('Інформація', <TbInfoCircle />, () => store.toggleInfo(), {
                        type: store.infoOpen ? 'primary' : 'text',
                        'aria-pressed': store.infoOpen,
                        'data-testid': 'disk-viewer-info',
                    })}
                    {!compact && closeButton}
                </div>
            </header>
            <div className={classNames(styles['main'])}>
                <section
                    className={classNames(styles['stage'], mode !== null && DARK_STAGE.has(mode) && styles['stageDark'])}
                    aria-label="Вміст файлу"
                    data-mode={mode ?? ''}
                >
                    <ViewerContent compact={compact} onDownload={onDownload} />
                </section>
                {infoPanel}
            </div>
        </div>,
        document.body
    )
})
